/**
 * Keys: the founder's own keys for any outside service (Higgsfield, OpenAI, Klaviyo), under the name their code
 * reads. Write-only: the list shows names, notes, who set them and whether they've arrived, never a value. Add,
 * Replace (a new key, or only the note) and Remove (asks first). The API syncs each change to the app and the computer.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { delivery, keyNameProblem, keyNameWarning, keyNoteProblem, MAX_NOTE, ownKeys, toKeyName } from '../../domain/keys'
import { when } from '../../domain/format'
import type { Secret, SetSecretInput } from '../../domain/schemas'
import { ApiError } from '../../infra/api'
import { visibleInterval } from '../../infra/visibleInterval'
import { ConfirmAction } from '../components/ConfirmAction'
import { Empty, Field, LockNote, Notice, Section, StatusChip } from '../components/ui'
import { useLoad } from '../useLoad'
import { useNow } from '../useNow'
import { useFounder } from './context'

type Said = { ok: boolean; text: string }

/** The API's refusals start lowercase ("keep the note to 200 characters"); shown alone, they read as sentences. */
const sentence = (m: string) => (m ? m[0]!.toUpperCase() + m.slice(1).replace(/([^.!?])$/, '$1.') : m)

export function KeysSection() {
  const { api } = useFounder()
  const list = useLoad(() => api.secrets(), [api])
  const now = useNow(15_000)
  const [adding, setAdding] = useState(false)
  const [replacing, setReplacing] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const [said, setSaid] = useState<Said | null>(null)
  const keys = ownKeys(list.data ?? [])
  const names = new Set(keys.map((k) => k.name))

  // A fresh key is on its way: look again every few seconds until it lands.
  const waiting = keys.some((k) => delivery(k, now).state === 'on_its_way')
  useEffect(() => (waiting ? visibleInterval(list.reload, 5000) : undefined), [waiting, list.reload])

  const saved = (name: string, text: string) => {
    setAdding(false)
    setReplacing(null)
    setSaid({ ok: true, text: `${name}: ${text}` })
    list.reload()
  }

  async function remove(name: string) {
    setRemoving(name)
    try {
      await api.removeSecret(name)
      setSaid({ ok: true, text: `${name} is removed. Your app redeploys without it, and your computer drops it.` })
    } catch (e) {
      setSaid({ ok: false, text: e instanceof ApiError && e.status < 500 ? sentence(e.message) : `We couldn't remove ${name} just now. Try again in a moment.` })
    } finally {
      setRemoving(null)
      list.reload()
    }
  }

  return (
    <Section
      title="Keys"
      count={list.data ? keys.length : undefined}
      className="keys flush-table"
      action={
        !adding ? (
          <button type="button" className="btn btn-ink btn-xs" onClick={() => (setAdding(true), setReplacing(null), setSaid(null))}>
            Add a key
          </button>
        ) : undefined
      }
    >
      <div className="keys-intro">
        <p className="aside">For any other service your app calls, like Higgsfield, OpenAI or Klaviyo. Add the key under the name your code reads.</p>
        <LockNote>Your app and your computer get it; nobody can read it back, us included.</LockNote>
      </div>
      {adding && <KeyForm taken={names} onCancel={() => setAdding(false)} onSaved={saved} />}
      {said && (
        <p className={said.ok ? 'keys-said' : 'keys-said bad-text'} role={said.ok ? 'status' : 'alert'}>
          {said.text}
        </p>
      )}
      {list.error && <Notice tone="bad">We couldn't load your keys just now. Try again in a moment.</Notice>}
      {!list.data && !list.error && <Empty>One moment.</Empty>}
      {list.data && keys.length === 0 && !adding && <Empty>No keys yet. When your agent connects a service, it tells you the name to add here.</Empty>}
      {keys.length > 0 && (
        <table className="table keys-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Status</th>
              <th>Set by</th>
              <th>Set</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <KeyRow
                key={k.name}
                secret={k}
                now={now}
                replacing={replacing === k.name}
                removing={removing === k.name}
                busy={removing !== null}
                onReplace={() => (setReplacing((x) => (x === k.name ? null : k.name)), setAdding(false), setSaid(null))}
                onRemove={() => void remove(k.name)}
                onSaved={saved}
              />
            ))}
          </tbody>
        </table>
      )}
    </Section>
  )
}

function KeyRow({
  secret: k,
  now,
  replacing,
  removing,
  busy,
  onReplace,
  onRemove,
  onSaved,
}: {
  secret: Secret
  now: number
  replacing: boolean
  removing: boolean
  busy: boolean
  onReplace: () => void
  onRemove: () => void
  onSaved: (name: string, text: string) => void
}) {
  const d = delivery(k, now)
  return (
    <>
      <tr>
        <td data-label="Key" className="cell-main">
          <span className="stack">
            <b className="mono key-name">{k.name}</b>
            {k.note && <span className="key-note">{k.note}</span>}
            {d.state === 'waiting' && <span className="label key-wait">{d.detail}</span>}
          </span>
        </td>
        <td data-label="Status" title={d.detail}>
          <StatusChip status={d.tone} label={d.label} />
        </td>
        <td data-label="Set by" className="key-by">
          {k.setBy ?? <span className="muted">someone who left</span>}
        </td>
        <td data-label="Set">{when(k.setAt, now)}</td>
        <td data-label="Actions" className="cell-actions">
          <div className="key-acts">
            <button type="button" className="btn btn-soft btn-xs" onClick={onReplace} aria-expanded={replacing} disabled={busy}>
              {replacing ? 'Close' : 'Replace'}
            </button>
            <ConfirmAction
              label="Remove"
              tone="ghost"
              question={`Remove ${k.name}? Your app redeploys without it and your computer drops it. Code that reads it falls back to example data or stops working.`}
              yes="Remove"
              onConfirm={onRemove}
              disabled={busy || replacing}
              working={removing}
            />
          </div>
        </td>
      </tr>
      {replacing && (
        <tr className="key-edit">
          <td colSpan={5} className="key-edit-cell">
            <KeyForm existing={k} onCancel={onReplace} onSaved={onSaved} />
          </td>
        </tr>
      )}
    </>
  )
}

/** Add a key (name, key, note) or replace one (a new key, the note, or both). Values are sent once and dropped. */
function KeyForm({
  existing,
  taken,
  onCancel,
  onSaved,
}: {
  existing?: Secret
  taken?: Set<string>
  onCancel: () => void
  onSaved: (name: string, text: string) => void
}) {
  const { api } = useFounder()
  const [name, setName] = useState(existing?.name ?? '')
  const [value, setValue] = useState('')
  const [note, setNote] = useState(existing?.note ?? '')
  const [tried, setTried] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nameProblem = existing ? null : keyNameProblem(name)
  const noteProblem = keyNoteProblem(note, value)
  const noteChanged = note.trim() !== (existing?.note ?? '')
  const valueProblem = !value.trim() && (!existing || !noteChanged) ? (existing ? 'Paste the new key, or change the note.' : 'Paste the key.') : null
  const showName = name.length > 0 || tried
  const replacesOne = !existing && !nameProblem && taken?.has(name)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setTried(true)
    if (nameProblem || noteProblem || valueProblem) return
    const v = value.trim()
    const input: SetSecretInput = v ? { value: v, ...(noteChanged || !existing ? { note: note.trim() || null } : {}) } : { note: note.trim() || null }
    setSaving(true)
    setError(null)
    try {
      await api.setSecret(name, input)
      setValue('')
      onSaved(name, v ? 'saved. Your app redeploys with it in a minute or two, and your computer gets it too.' : 'note saved.')
    } catch (err) {
      setError(err instanceof ApiError && err.status < 500 ? sentence(err.message) : "We couldn't save it just now. Try again in a moment.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="key-form" onSubmit={submit} noValidate aria-label={existing ? `Replace ${existing.name}` : 'Add a key'}>
      {existing ? (
        <p className="key-form-title">
          Replace <b className="mono">{existing.name}</b>
        </p>
      ) : (
        <Field
          label="Name"
          hint={
            replacesOne
              ? `You already have ${name}. Saving replaces its key.`
              : (!nameProblem && keyNameWarning(name)) || 'Use the name your code reads, like HIGGSFIELD_API_KEY'
          }
          {...(showName && nameProblem ? { error: nameProblem } : {})}
        >
          <input
            className="input mono"
            type="text"
            value={name}
            onChange={(e) => setName(toKeyName(e.target.value))}
            placeholder="HIGGSFIELD_API_KEY"
            maxLength={64}
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="characters"
            aria-invalid={showName && !!nameProblem}
            autoFocus
          />
        </Field>
      )}
      <Field
        label={existing ? 'New key' : 'Key'}
        hint={existing ? 'Leave it empty to keep the current key and change only the note.' : "Paste it from the service. It's never shown again, here or anywhere."}
        {...(tried && valueProblem ? { error: valueProblem } : {})}
      >
        <input
          className="input mono"
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Paste the key"
          autoComplete="new-password"
          spellCheck={false}
          autoCapitalize="off"
          {...(existing ? { autoFocus: true } : {})}
        />
      </Field>
      <Field
        label="Note (optional)"
        hint={`What it's for, like “Higgsfield API key for the ad generator”. Your team sees it. ${[...note.trim()].length}/${MAX_NOTE}`}
        {...(noteProblem ? { error: noteProblem } : {})}
      >
        <input className="input" type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={MAX_NOTE + 20} autoComplete="off" />
      </Field>
      {error && <Notice tone="bad">{error}</Notice>}
      <div className="form-foot">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-ink btn-sm" disabled={saving}>
          {saving ? 'Saving…' : existing ? (value.trim() ? 'Replace key' : 'Save note') : 'Save key'}
        </button>
      </div>
    </form>
  )
}
